import React, { useEffect, useMemo, useRef, useState } from "react";
import { Select } from "antd";
import { FormInstance } from "antd/lib/form/hooks/useForm";
import { Nullable } from "../../../../../models/Common";
import {
  FleetCardAssignment,
  FleetDriver,
} from "../../../../../models/FleetExpenses/FleetExpensesModel";
import FleetExpensesStore from "../../../../../stores/FleetExpensesStore";
import { formItemFactory } from "../../../../formItems/FormItemFactory";
import { CARD_ASSIGNMENT_SELECT_PREFIX } from "../constants";
import { FleetExpenseFormValues } from "../types";
import { searchByLabelProps } from "../selectSearchProps";
import validations from "../validations";
import { createCardAssignmentOptions } from "./cardAssignmentOptions";

const FleetExpenseItem = formItemFactory<FleetExpenseFormValues>(validations);

export type CardAssignmentSelectProps = {
  date: Nullable<string>;
  driverId: Nullable<FleetDriver["id"]>;
  form: FormInstance<FleetExpenseFormValues>;
  onAssignmentsChange: (assignments: Nullable<FleetCardAssignment[]>) => void;
  onLoadingChange: (isLoading: boolean) => void;
  refreshKey: number;
  store: typeof FleetExpensesStore;
};

const CardAssignmentSelect = ({
  date,
  driverId,
  form,
  onAssignmentsChange,
  onLoadingChange,
  refreshKey,
  store,
}: CardAssignmentSelectProps): React.ReactElement => {
  const [assignments, setAssignments] = useState<
    Nullable<FleetCardAssignment[]>
  >(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<Nullable<string>>(null);
  const pendingAssignmentValue = useRef<Nullable<string>>(null);

  useEffect(() => {
    let isCurrentRequest = true;
    const currentAssignmentValue: unknown = form.getFieldValue(
      "card_assignment_id"
    );
    const selectedAssignmentValue =
      typeof currentAssignmentValue === "string" &&
      currentAssignmentValue.indexOf(CARD_ASSIGNMENT_SELECT_PREFIX) === 0
        ? currentAssignmentValue
        : pendingAssignmentValue.current;
    pendingAssignmentValue.current = selectedAssignmentValue;
    setAssignments(null);
    setLoadError(null);
    onAssignmentsChange(null);
    form.setFieldsValue({ card_assignment_id: undefined });
    form.setFields([{ name: "card_assignment_id", errors: [] }]);

    if (driverId === null || driverId === undefined || !date) {
      pendingAssignmentValue.current = null;
      setIsLoading(false);
      onLoadingChange(false);
      return () => {
        isCurrentRequest = false;
      };
    }

    setIsLoading(true);
    onLoadingChange(true);
    store
      .getDriverCardAssignments(driverId, date)
      .then((result) => {
        if (!isCurrentRequest) {
          return;
        }

        setAssignments(result);
        onAssignmentsChange(result);
        if (result === null || result === undefined) {
          pendingAssignmentValue.current = null;
          setLoadError("Unable to load card assignments.");
          return;
        }

        if (
          selectedAssignmentValue !== null &&
          selectedAssignmentValue !== undefined &&
          result.some(
            (assignment) =>
              `${CARD_ASSIGNMENT_SELECT_PREFIX}${assignment.id}` ===
              selectedAssignmentValue
          )
        ) {
          form.setFieldsValue({
            card_assignment_id: selectedAssignmentValue,
          });
          pendingAssignmentValue.current = null;
        } else if (result.length === 1) {
          form.setFieldsValue({
            card_assignment_id: `${CARD_ASSIGNMENT_SELECT_PREFIX}${result[0].id}`,
          });
          pendingAssignmentValue.current = null;
        } else {
          pendingAssignmentValue.current = null;
        }
      })
      .catch((error) => {
        if (!isCurrentRequest) {
          return;
        }

        const errorValue: unknown = error;
        setAssignments(null);
        onAssignmentsChange(null);
        pendingAssignmentValue.current = null;
        form.setFieldsValue({ card_assignment_id: undefined });
        setLoadError(
          errorValue instanceof Error
            ? errorValue.message
            : "Unable to load card assignments."
        );
      })
      .finally(() => {
        if (isCurrentRequest) {
          setIsLoading(false);
          onLoadingChange(false);
        }
      });

    return () => {
      isCurrentRequest = false;
      onLoadingChange(false);
    };
  }, [
    date,
    driverId,
    form,
    onAssignmentsChange,
    onLoadingChange,
    refreshKey,
    store,
  ]);

  const assignmentOptions = useMemo(
    () =>
      assignments === null || assignments === undefined
        ? []
        : createCardAssignmentOptions(assignments).getSelectOptions(
            CARD_ASSIGNMENT_SELECT_PREFIX
          ),
    [assignments]
  );
  const assignmentCount = assignments?.length;
  const isSingleOrEmpty = assignmentCount !== undefined && assignmentCount <= 1;
  const isDisabled = useMemo(
    () =>
      isLoading ||
      !assignments ||
      isSingleOrEmpty ||
      driverId === null ||
      driverId === undefined ||
      !date,
    [assignments, date, driverId, isLoading, isSingleOrEmpty]
  );

  return (
    <FleetExpenseItem
      name="card_assignment_id"
      label="Card Assignment"
      extra={loadError ?? undefined}
    >
      <Select
        placeholder="Select card assignment"
        options={assignmentOptions}
        onChange={() =>
          form.setFields([{ name: "card_assignment_id", errors: [] }])
        }
        disabled={isDisabled}
        loading={isLoading}
        allowClear={false}
        {...searchByLabelProps}
      />
    </FleetExpenseItem>
  );
};

export default CardAssignmentSelect;
